import React, { useState, useEffect, useCallback } from 'react';
import { admin } from '../services/api';
import BrandCredit from './BrandCredit';
import './AdminPanel.css';

function WebhookEditor({ instance, onClose }) {
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const response = await admin.getWebhook(instance.id);
        const webhook = response.data?.webhook || response.data || {};
        setUrl(webhook.url || '');
        setEnabled(Boolean(webhook.enabled));
      } catch (err) {
        setError(err.response?.data?.error || 'Erro ao carregar webhook');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [instance.id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await admin.setWebhook(instance.id, { url, enabled });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar webhook');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="webhook-editor">
      {loading ? (
        <p>Carregando webhook...</p>
      ) : (
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>URL do Webhook</label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://exemplo.com/webhook"
            />
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Ativo
            </label>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="webhook-editor-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Fechar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function AccessManager({ instance, users, onGrant, onRevoke }) {
  const [selectedUserId, setSelectedUserId] = useState('');

  const availableUsers = users.filter(
    (u) => !instance.accessUsers?.some((granted) => granted.id === u.id)
  );

  const handleAdd = (e) => {
    e.preventDefault();
    if (!selectedUserId) return;
    onGrant(instance, Number(selectedUserId));
    setSelectedUserId('');
  };

  return (
    <div className="access-manager">
      <div className="access-chips">
        {(instance.accessUsers || []).length === 0 && (
          <span className="access-empty">Ninguém tem acesso ainda</span>
        )}
        {(instance.accessUsers || []).map((u) => (
          <span key={u.id} className="access-chip">
            {u.username}
            <button
              type="button"
              className="access-chip-remove"
              title="Remover acesso"
              onClick={() => onRevoke(instance, u.id)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {availableUsers.length > 0 && (
        <form className="access-add-form" onSubmit={handleAdd}>
          <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
            <option value="" disabled>Conceder acesso a...</option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
          <button type="submit" className="btn btn-secondary btn-sm">Adicionar</button>
        </form>
      )}
    </div>
  );
}

function AdminPanel({ user, onBack }) {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [instances, setInstances] = useState([]);
  const [error, setError] = useState('');
  const [webhookInstanceId, setWebhookInstanceId] = useState(null);

  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [creatingUser, setCreatingUser] = useState(false);

  const [newInstanceName, setNewInstanceName] = useState('');
  const [newInstanceUserId, setNewInstanceUserId] = useState('');
  const [creatingInstance, setCreatingInstance] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const response = await admin.listUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar usuários');
    }
  }, []);

  const loadInstances = useCallback(async () => {
    try {
      const response = await admin.listInstances();
      setInstances(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar instâncias');
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadInstances();
  }, [loadUsers, loadInstances]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setError('');
    try {
      await admin.createUser(newUsername.trim(), newPassword, newRole);
      setNewUsername('');
      setNewPassword('');
      setNewRole('user');
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleChangeRole = async (target, role) => {
    setError('');
    try {
      await admin.updateUserRole(target.id, role);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar papel do usuário');
      await loadUsers(); // reverte o select para o valor real
    }
  };

  const handleDeleteUser = async (target) => {
    if (!window.confirm(`Deseja realmente excluir o usuário "${target.username}"?`)) {
      return;
    }
    setError('');
    try {
      await admin.deleteUser(target.id);
      await loadUsers();
      await loadInstances();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const handleCreateInstance = async (e) => {
    e.preventDefault();
    setCreatingInstance(true);
    setError('');
    try {
      await admin.createInstance(Number(newInstanceUserId), newInstanceName.trim());
      setNewInstanceName('');
      setNewInstanceUserId('');
      await loadInstances();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar instância');
    } finally {
      setCreatingInstance(false);
    }
  };

  const handleDeleteInstance = async (instance) => {
    if (!window.confirm(`Deseja realmente excluir a instância "${instance.name}"?`)) {
      return;
    }
    setError('');
    try {
      await admin.deleteInstance(instance.id);
      await loadInstances();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir instância');
    }
  };

  const handleGrantAccess = async (instance, userId) => {
    setError('');
    try {
      await admin.grantAccess(instance.id, userId);
      await loadInstances();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao conceder acesso');
    }
  };

  const handleRevokeAccess = async (instance, userId) => {
    setError('');
    try {
      await admin.revokeAccess(instance.id, userId);
      await loadInstances();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover acesso');
    }
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="header-brand">
          <span className="brand-mark">Z</span>
          <h1>EvoRouterZap — Admin</h1>
        </div>
        <div className="user-info">
          <span className="user-pill">{user.username}</span>
          <button onClick={onBack} className="btn btn-secondary">Voltar ao Dashboard</button>
        </div>
      </header>

      <main className="admin-content">
        {error && <div className="error-message">{error}</div>}

        <div className="admin-tabs">
          <button
            className={`admin-tab ${tab === 'users' ? 'active' : ''}`}
            onClick={() => setTab('users')}
          >
            Usuários
          </button>
          <button
            className={`admin-tab ${tab === 'instances' ? 'active' : ''}`}
            onClick={() => setTab('instances')}
          >
            Instâncias
          </button>
        </div>

        {tab === 'users' && (
          <div className="admin-card">
            <h2>Novo usuário</h2>
            <form onSubmit={handleCreateUser} className="inline-form">
              <input
                type="text"
                placeholder="Usuário"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                <option value="user">Usuário</option>
                <option value="admin">Admin</option>
              </select>
              <button type="submit" className="btn btn-primary" disabled={creatingUser}>
                {creatingUser ? 'Criando...' : 'Criar'}
              </button>
            </form>

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Papel</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>
                      <select
                        value={u.role}
                        disabled={u.id === user.id}
                        onChange={(e) => handleChangeRole(u, e.target.value)}
                      >
                        <option value="user">Usuário</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>{new Date(u.created_at).toLocaleString('pt-BR')}</td>
                    <td className="admin-table-actions">
                      <button
                        className="btn btn-warning btn-sm"
                        disabled={u.id === user.id}
                        title={u.id === user.id ? 'Você não pode excluir a si mesmo' : ''}
                        onClick={() => handleDeleteUser(u)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'instances' && (
          <div className="admin-card">
            <h2>Nova instância</h2>
            <form onSubmit={handleCreateInstance} className="inline-form">
              <input
                type="text"
                placeholder="Nome amigável"
                value={newInstanceName}
                onChange={(e) => setNewInstanceName(e.target.value)}
                required
              />
              <select
                value={newInstanceUserId}
                onChange={(e) => setNewInstanceUserId(e.target.value)}
                required
              >
                <option value="" disabled>Dono inicial</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
              <button type="submit" className="btn btn-primary" disabled={creatingInstance}>
                {creatingInstance ? 'Criando...' : 'Criar'}
              </button>
            </form>

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Dono</th>
                  <th>Instância (Evolution API)</th>
                  <th>Acesso</th>
                  <th>Criada em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {instances.map((instance) => (
                  <React.Fragment key={instance.id}>
                    <tr>
                      <td>{instance.name}</td>
                      <td>{instance.owner_username || '—'}</td>
                      <td>{instance.evolution_instance_name}</td>
                      <td>
                        <AccessManager
                          instance={instance}
                          users={users}
                          onGrant={handleGrantAccess}
                          onRevoke={handleRevokeAccess}
                        />
                      </td>
                      <td>{new Date(instance.created_at).toLocaleString('pt-BR')}</td>
                      <td className="admin-table-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() =>
                            setWebhookInstanceId(
                              webhookInstanceId === instance.id ? null : instance.id
                            )
                          }
                        >
                          Webhook
                        </button>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => handleDeleteInstance(instance)}
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                    {webhookInstanceId === instance.id && (
                      <tr>
                        <td colSpan={6}>
                          <WebhookEditor
                            instance={instance}
                            onClose={() => setWebhookInstanceId(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <BrandCredit />
      </main>
    </div>
  );
}

export default AdminPanel;
